// import GT from '../../Images/GT.jpg'
import React, { useState } from "react";

// ─────────────────────────────────────────────────────────────────
// SHARED STYLES
// ─────────────────────────────────────────────────────────────────
const S = {
  wrapper: {
    marginTop: '10px', marginLeft: '0px', border: '3px', padding: '40px',
    borderStyle: 'solid', borderColor: 'grey', fontFamily: "'Georgia', serif",
    color: '#1a1a2e', lineHeight: '1.7',
  },
  h1: {
    fontSize: '2rem', fontWeight: '700', borderBottom: '3px solid #2E75B6',
    paddingBottom: '8px', marginBottom: '24px', color: '#1F3864',
  },
  h2: {
    fontSize: '1.3rem', fontWeight: '700', color: '#2E75B6', marginTop: '36px',
    marginBottom: '12px', borderLeft: '4px solid #2E75B6', paddingLeft: '10px',
  },
  p: { marginBottom: '12px', fontSize: '0.97rem' },
  infoBox: {
    background: '#EBF3FB', border: '2px solid #2E75B6', borderRadius: '6px',
    padding: '16px 20px', margin: '16px 0',
  },
  infoBoxTitle: {
    fontWeight: '700', fontFamily: "'Courier New', monospace", fontSize: '0.88rem',
    color: '#1F3864', marginBottom: '8px',
  },
  infoBoxItem: {
    fontSize: '0.9rem', margin: '4px 0', paddingLeft: '12px', borderLeft: '2px solid #2E75B6',
  },
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '0.92rem' },
  th: { background: '#2E75B6', color: '#fff', padding: '10px 14px', textAlign: 'left', fontWeight: '700', fontSize: '0.9rem' },
  tdE: { padding: '10px 14px', borderBottom: '1px solid #ccd9ea', verticalAlign: 'top', background: '#f5f9ff' },
  tdO: { padding: '10px 14px', borderBottom: '1px solid #ccd9ea', verticalAlign: 'top', background: '#fff' },
  tdL: { fontWeight: '600', color: '#1F3864', whiteSpace: 'nowrap', width: '220px' },
  code: { fontFamily: "'Courier New', monospace", fontSize: '0.88rem', whiteSpace: 'pre-wrap' },
  dWrap: {
    margin: '24px 0', background: '#f8fbff', border: '1px solid #ccd9ea',
    borderRadius: '8px', padding: '20px',
  },
  dTitle: {
    fontWeight: '700', fontSize: '0.95rem', color: '#1F3864',
    marginBottom: '12px', textAlign: 'center', letterSpacing: '0.02em',
  },
  dCaption: { fontSize: '0.84rem', color: '#555', textAlign: 'center', marginTop: '10px', fontStyle: 'italic' },
};

// ─────────────────────────────────────────────────────────────────
// SHARED COMPONENTS
// ─────────────────────────────────────────────────────────────────
function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width: '220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Draw bracket [ or ] for matrix notation
function Bracket({ x, y, h, side, color='#1F3864', w=10 }) {
  const top = y, bot = y + h;
  if (side === 'left')  return <path d={`M ${x+w},${top} L ${x},${top} L ${x},${bot} L ${x+w},${bot}`} fill="none" stroke={color} strokeWidth="2"/>;
  if (side === 'right') return <path d={`M ${x-w},${top} L ${x},${top} L ${x},${bot} L ${x-w},${bot}`} fill="none" stroke={color} strokeWidth="2"/>;
}

// Render a matrix as SVG cells
function SVGMatrix({ data, x0, y0, cellW=52, cellH=36, highlightRow=-1, highlightCol=-1, hlRowColor='#d0e8ff', hlColColor='#ffd0d0', activeColor='#2E75B6', labelFn=null }) {
  const rows = data.length, cols = data[0].length;
  const W = cols*cellW, H = rows*cellH;
  return (
    <g>
      <Bracket x={x0-4} y={y0-4} h={H+8} side="left" />
      <Bracket x={x0+W+4} y={y0-4} h={H+8} side="right" />
      {data.map((row, r) => row.map((val, c) => {
        const cx = x0 + c*cellW + cellW/2;
        const cy = y0 + r*cellH + cellH/2;
        const isHL = r===highlightRow && c===highlightCol;
        const isHR = r===highlightRow && highlightRow>=0;
        const isHC = c===highlightCol && highlightCol>=0;
        const fill = isHL ? activeColor : isHR ? hlRowColor : isHC ? hlColColor : 'none';
        const textColor = isHL ? '#fff' : '#1F3864';
        return (
          <g key={`${r}-${c}`}>
            {(fill!=='none') && <rect x={x0+c*cellW+2} y={y0+r*cellH+2} width={cellW-4} height={cellH-4} rx={3} fill={fill} opacity={isHL?1:0.5}/>}
            <text x={cx} y={cy+5} textAnchor="middle" fontSize="14" fill={textColor} fontWeight={isHL?'700':'400'} fontFamily="'Courier New', monospace">
              {labelFn ? labelFn(r,c) : val}
            </text>
          </g>
        );
      }))}
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Matrix Anatomy
// ─────────────────────────────────────────────────────────────────
function DiagramAnatomy() {
  const W=500, H=240;
  const data = [[3,-1,2],[0,5,-4],[7,2,1]];
  const x0=120, y0=50, cW=56, cH=44;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Matrix Anatomy — Rows, Columns, and Element Notation</div>
      <div style={{ textAlign:'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth:'100%' }}>
          {/* Matrix */}
          <SVGMatrix data={data} x0={x0} y0={y0} cellW={cW} cellH={cH}/>

          {/* Row arrows */}
          {[0,1,2].map(r => (
            <g key={r}>
              <line x1={x0-30} y1={y0+r*cH+cH/2} x2={x0-10} y2={y0+r*cH+cH/2} stroke="#2E75B6" strokeWidth="1.5" markerEnd="url(#arrowB)"/>
              <text x={x0-34} y={y0+r*cH+cH/2+5} textAnchor="end" fontSize="11" fill="#2E75B6">Row {r+1}</text>
            </g>
          ))}

          {/* Col arrows */}
          {[0,1,2].map(c => (
            <g key={c}>
              <line x1={x0+c*cW+cW/2} y1={y0-12} x2={x0+c*cW+cW/2} y2={y0-2} stroke="#c0392b" strokeWidth="1.5" markerEnd="url(#arrowR)"/>
              <text x={x0+c*cW+cW/2} y={y0-16} textAnchor="middle" fontSize="11" fill="#c0392b">Col {c+1}</text>
            </g>
          ))}

          {/* Element callout for a[1][2] = -4 */}
          <rect x={x0+2*cW+2} y={y0+1*cH+2} width={cW-4} height={cH-4} rx={3} fill="#e07b00" opacity="0.25"/>
          <line x1={x0+2*cW+cW/2+16} y1={y0+1*cH+cH/2} x2={380} y2={y0+1*cH+cH/2} stroke="#e07b00" strokeWidth="1.3" strokeDasharray="4,3"/>
          <rect x={382} y={y0+1*cH+cH/2-18} width={112} height={38} rx={4} fill="#fff7ee" stroke="#e07b00" strokeWidth="1.3"/>
          <text x={438} y={y0+1*cH+cH/2-4}  textAnchor="middle" fontSize="11" fill="#e07b00" fontWeight="700">a₂₃ = −4</text>
          <text x={438} y={y0+1*cH+cH/2+10} textAnchor="middle" fontSize="10" fill="#888">row 2, col 3</text>

          {/* Size label */}
          <rect x={x0-6} y={y0+3*cH+14} width={3*cW+12} height={22} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
          <text x={x0+3*cW/2} y={y0+3*cH+29} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">3×3 matrix  [A]</text>

          <defs>
            <marker id="arrowB" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
              <polygon points="0 0,7 3.5,0 7" fill="#2E75B6"/>
            </marker>
            <marker id="arrowR" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
              <polygon points="0 0,7 3.5,0 7" fill="#c0392b"/>
            </marker>
          </defs>
        </svg>
      </div>
      <div style={S.dCaption}>
        Element aᵢⱼ is in row i, column j. A 3×3 matrix has 3 rows and 3 columns. Subscript order is always row first, column second.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Matrix Multiplication (interactive)
// ─────────────────────────────────────────────────────────────────
function DiagramMultiplication() {
  const A = [[1,2],[3,4],[0,1]];
  const B = [[5,0,2],[1,3,-1]];
  // C = A*B
  const C = A.map(row => B[0].map((_, c) => row.reduce((s,_,k)=>s+row[k]*B[k][c],0)));

  const [sel, setSel] = useState({ r:0, c:0 });
  const { r, c } = sel;

  const W=560, H=270;
  const cW=46, cH=38;
  const ax0=30, ay0=80;
  const bx0=200, by0=40;
  const cx0=360, cy0=80;

  // dot product terms for selected cell
  const terms = A[r].map((v,k) => `${v}×${B[k][c]}`).join(' + ');
  const result = C[r][c];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Matrix Multiplication — Click any result cell to see the dot product</div>
      <div style={{ textAlign:'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth:'100%', cursor:'pointer' }}>
          {/* Labels */}
          <text x={ax0+2*cW/2} y={ay0-18} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">[A]  3×2</text>
          <text x={bx0+3*cW/2} y={by0-18} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">[B]  2×3</text>
          <text x={cx0+3*cW/2} y={cy0-18} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">[C]  3×3</text>
          <text x={170} y={ay0+A.length*cH/2+10} fontSize="22" fill="#1F3864" fontWeight="300">×</text>
          <text x={335} y={ay0+A.length*cH/2+10} fontSize="22" fill="#1F3864" fontWeight="300">=</text>

          {/* Matrix A — highlight selected row */}
          <SVGMatrix data={A} x0={ax0} y0={ay0} cellW={cW} cellH={cH} highlightRow={r} highlightCol={-1} hlRowColor="#d0e8ff"/>

          {/* Matrix B — highlight selected col */}
          <SVGMatrix data={B} x0={bx0} y0={by0} cellW={cW} cellH={cH} highlightRow={-1} highlightCol={c} hlColColor="#ffd0d0"/>

          {/* Matrix C — clickable cells */}
          {C.map((row, ri) => row.map((val, ci) => {
            const cx2 = cx0+ci*cW+cW/2, cy2 = cy0+ri*cH+cH/2;
            const isActive = ri===r && ci===c;
            return (
              <g key={`${ri}-${ci}`} onClick={() => setSel({r:ri,c:ci})} style={{cursor:'pointer'}}>
                <rect x={cx0+ci*cW+2} y={cy0+ri*cH+2} width={cW-4} height={cH-4} rx={3}
                  fill={isActive?'#2E75B6':'#f0f7ff'} stroke={isActive?'#2E75B6':'#aac'} strokeWidth={isActive?2:1}/>
                <text x={cx2} y={cy2+5} textAnchor="middle" fontSize="14"
                  fill={isActive?'#fff':'#1F3864'} fontWeight={isActive?'700':'400'}
                  fontFamily="'Courier New', monospace">{val}</text>
              </g>
            );
          }))}
          <Bracket x={cx0-4} y={cy0-4} h={C.length*cH+8} side="left"/>
          <Bracket x={cx0+3*cW+4} y={cy0-4} h={C.length*cH+8} side="right"/>

          {/* Dot product explanation box */}
          <rect x={30} y={210} width={500} height={48} rx={5} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.3"/>
          <text x={280} y={229} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">
            C[{r+1}][{c+1}]  =  Row {r+1} of A  ·  Col {c+1} of B
          </text>
          <text x={280} y={248} textAnchor="middle" fontSize="12" fill="#2E75B6" fontFamily="'Courier New', monospace">
            {terms}  =  {result}
          </text>
        </svg>
      </div>
      <div style={S.dCaption}>
        Each element Cᵢⱼ is the dot product of row i from A and column j from B. [A] must be m×n and [B] must be n×p — inner dimensions must match.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Determinant as Parallelogram Area
// ─────────────────────────────────────────────────────────────────
function DiagramDeterminant() {
  const W=500, H=260;
  const ox=80, oy=200, sc=55;

  // Matrix [[3,1],[1,2]] — det = 3*2-1*1 = 5
  const a=[3,1], b=[1,2];
  const det = a[0]*b[1]-a[1]*b[0];

  const toSVG = (x,y) => [ox+x*sc, oy-y*sc];
  const [ax,ay] = toSVG(...a);
  const [bx,by] = toSVG(...b);
  const [cx,cy] = toSVG(a[0]+b[0], a[1]+b[1]);

  const parallelPath = `M ${ox},${oy} L ${ax},${ay} L ${cx},${cy} L ${bx},${by} Z`;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Determinant — Geometric Meaning as Parallelogram Area</div>
      <div style={{ textAlign:'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth:'100%' }}>
          {/* Grid */}
          {[0,1,2,3,4].map(x => { const [sx]=toSVG(x,0); return <line key={x} x1={sx} y1={20} x2={sx} y2={H-10} stroke="#eee" strokeWidth="1"/>; })}
          {[0,1,2,3].map(y => { const [,sy]=toSVG(0,y);  return <line key={y} x1={20} y1={sy} x2={W-20} y2={sy} stroke="#eee" strokeWidth="1"/>; })}

          {/* Axes */}
          <line x1={20} y1={oy} x2={W-10} y2={oy} stroke="#1F3864" strokeWidth="1.5"/>
          <line x1={ox} y1={10} x2={ox}   y2={H-10} stroke="#1F3864" strokeWidth="1.5"/>
          {[1,2,3,4].map(x=>{const[sx]=toSVG(x,0);return <text key={x} x={sx} y={oy+14} textAnchor="middle" fontSize="10" fill="#aaa">{x}</text>;})}
          {[1,2,3].map(y=>{const[,sy]=toSVG(0,y);return <text key={y} x={ox-6} y={sy+4} textAnchor="end" fontSize="10" fill="#aaa">{y}</text>;})}

          {/* Parallelogram */}
          <path d={parallelPath} fill="#2E75B6" fillOpacity="0.18" stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="5,3"/>

          {/* Vectors */}
          <defs>
            <marker id="arrA" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto"><polygon points="0 0,7 3.5,0 7" fill="#2E75B6"/></marker>
            <marker id="arrB" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto"><polygon points="0 0,7 3.5,0 7" fill="#c0392b"/></marker>
          </defs>
          <line x1={ox} y1={oy} x2={ax-4} y2={ay+2} stroke="#2E75B6" strokeWidth="2.5" markerEnd="url(#arrA)"/>
          <line x1={ox} y1={oy} x2={bx-2} y2={by+4} stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#arrB)"/>

          {/* Ghost vectors for parallelogram completion */}
          <line x1={bx} y1={by} x2={cx} y2={cy} stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="4,3"/>
          <line x1={ax} y1={ay} x2={cx} y2={cy} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="4,3"/>

          {/* Labels */}
          <text x={ax+8} y={ay} fontSize="13" fill="#2E75B6" fontWeight="700">a = [3, 1]</text>
          <text x={bx-50} y={by-8} fontSize="13" fill="#c0392b" fontWeight="700">b = [1, 2]</text>

          {/* Area label */}
          <text x={ox+2.2*sc} y={oy-1.5*sc} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Area = |det| = {det}</text>

          {/* Matrix and formula */}
          <rect x={310} y={30} width={175} height={95} rx={5} fill="#fff" stroke="#ccd9ea" strokeWidth="1"/>
          <text x={397} y={52} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">det [A]</text>
          <text x={397} y={72} textAnchor="middle" fontSize="13" fill="#1F3864" fontFamily="'Courier New', monospace">| 3  1 |</text>
          <text x={397} y={90} textAnchor="middle" fontSize="13" fill="#1F3864" fontFamily="'Courier New', monospace">| 1  2 |</text>
          <text x={397} y={112} textAnchor="middle" fontSize="12" fill="#2E75B6" fontWeight="700">= (3×2) − (1×1) = {det}</text>
        </svg>
      </div>
      <div style={S.dCaption}>
        The determinant of a 2×2 matrix = ad − bc = the area of the parallelogram formed by the two row vectors. det = 0 means the vectors are parallel (linearly dependent — no unique solution exists).
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Identity & Inverse
// ─────────────────────────────────────────────────────────────────
function DiagramInverse() {
  const W=540, H=220;

  // A = [[2,1],[5,3]], A_inv = [[3,-1],[-5,2]], I = [[1,0],[0,1]]
  const A = [[2,1],[5,3]];
  const Ai = [[3,-1],[-5,2]];
  const I = [[1,0],[0,1]];

  const cW=50, cH=40;
  const positions = [
    { data:A,  x0:20,  y0:80, label:'[A]' },
    { data:Ai, x0:160, y0:80, label:'[A]⁻¹' },
    { data:I,  x0:330, y0:80, label:'[I]' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Inverse Matrix — [A] × [A]⁻¹ = [I]  (Identity)</div>
      <div style={{ textAlign:'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth:'100%' }}>

          {positions.map(({ data, x0, y0, label }, pi) => (
            <g key={pi}>
              <text x={x0+data[0].length*cW/2} y={y0-14} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">{label}</text>
              <SVGMatrix data={data} x0={x0} y0={y0} cellW={cW} cellH={cH}/>
            </g>
          ))}

          {/* Operators */}
          <text x={143} y={80+cH+5} textAnchor="middle" fontSize="24" fill="#1F3864" fontWeight="300">×</text>
          <text x={305} y={80+cH+5} textAnchor="middle" fontSize="24" fill="#1F3864" fontWeight="300">=</text>

          {/* Identity highlight */}
          <rect x={330+2} y={80+2} width={cW-4} height={cH-4} rx={3} fill="#27ae60" opacity="0.2"/>
          <rect x={330+cW+2} y={80+cH+2} width={cW-4} height={cH-4} rx={3} fill="#27ae60" opacity="0.2"/>

          {/* Explanation callouts */}
          <rect x={20} y={165} width={500} height={40} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
          <text x={270} y={181} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">
            2×2 inverse formula:   [A]⁻¹ = (1/det) × [ d  −b ]
          </text>
          <text x={270} y={197} textAnchor="middle" fontSize="11" fill="#1F3864" fontFamily="'Courier New',monospace">
            det([A]) = (2×3)−(1×5) = 1          [ −c   a ]
          </text>
        </svg>
      </div>
      <div style={S.dCaption}>
        The identity matrix [I] has 1s on the diagonal and 0s elsewhere — multiplying any matrix by [I] leaves it unchanged. [A]⁻¹ exists only when det([A]) ≠ 0.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Eigenvalue / Eigenvector
// ─────────────────────────────────────────────────────────────────
function DiagramEigenvalue() {
  const W=520, H=270;
  const ox=200, oy=190, sc=65;
  const toSVG = (x,y) => [ox+x*sc, oy-y*sc];

  // Eigenvector of [[2,1],[0,2]]: λ=2, v=[1,0]
  // Normal vector [1,1] transforms to [3,2]
  const eigVec = [1,0];
  const normVec = [1,1];
  const eigOut = [2,0];    // A*eigVec = 2*eigVec
  const normOut = [3,2];   // A*normVec

  const arrow = (x1,y1,x2,y2,color,id) => {
    const [sx1,sy1]=toSVG(x1,y1), [sx2,sy2]=toSVG(x2,y2);
    return <line x1={sx1} y1={sy1} x2={sx2} y2={sy2} stroke={color} strokeWidth="2.5" markerEnd={`url(#${id})`}/>;
  };

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Eigenvalues & Eigenvectors — Stretch vs. Rotation Under Transformation</div>
      <div style={{ textAlign:'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth:'100%' }}>
          <defs>
            {['arrE','arrEo','arrN','arrNo'].map((id,i)=>{
              const colors=['#27ae60','#27ae60','#c0392b','#c0392b'];
              return <marker key={id} id={id} markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
                <polygon points="0 0,7 3.5,0 7" fill={colors[i]}/>
              </marker>;
            })}
          </defs>

          {/* Grid */}
          {[-2,-1,0,1,2,3].map(x=>{const[sx]=toSVG(x,0);return <line key={x} x1={sx} y1={20} x2={sx} y2={H-10} stroke="#eee" strokeWidth="1"/>;})}
          {[-2,-1,0,1,2].map(y=>{const[,sy]=toSVG(0,y);return <line key={y} x1={20} y1={sy} x2={W-10} y2={sy} stroke="#eee" strokeWidth="1"/>;})}

          {/* Axes */}
          <line x1={20} y1={oy} x2={W-10} y2={oy} stroke="#bbb" strokeWidth="1.2"/>
          <line x1={ox} y1={10} x2={ox}   y2={H-10} stroke="#bbb" strokeWidth="1.2"/>

          {/* Eigenvector: before (solid) and after (dashed = scaled) */}
          {arrow(0,0,...eigVec,'#27ae60','arrE')}
          {(() => {
            const [sx1,sy1]=toSVG(0,0),[sx2,sy2]=toSVG(...eigOut);
            return <line x1={sx1} y1={sy1} x2={sx2} y2={sy2} stroke="#27ae60" strokeWidth="2.5" strokeDasharray="6,3" markerEnd="url(#arrEo)"/>;
          })()}

          {/* Normal vector: before and after (rotates) */}
          {arrow(0,0,...normVec,'#c0392b','arrN')}
          {(() => {
            const [sx1,sy1]=toSVG(0,0),[sx2,sy2]=toSVG(...normOut);
            return <line x1={sx1} y1={sy1} x2={sx2} y2={sy2} stroke="#c0392b" strokeWidth="2.5" strokeDasharray="6,3" markerEnd="url(#arrNo)"/>;
          })()}

          {/* Labels */}
          {(() => {
            const [ex,ey]=toSVG(...eigVec);
            const [eox,eoy]=toSVG(...eigOut);
            const [nx,ny]=toSVG(...normVec);
            const [nox,noy]=toSVG(...normOut);
            return <>
              <text x={ex+6} y={ey-8}  fontSize="11" fill="#27ae60" fontWeight="700">v (eigenvector)</text>
              <text x={eox+6} y={eoy-8} fontSize="11" fill="#27ae60">λv = 2v (scaled only)</text>
              <text x={nx+6} y={ny-8}  fontSize="11" fill="#c0392b" fontWeight="700">w (normal vector)</text>
              <text x={nox+6} y={noy+14} fontSize="11" fill="#c0392b">Aw (rotated + scaled)</text>
            </>;
          })()}

          {/* Origin dot */}
          <circle cx={ox} cy={oy} r={4} fill="#1F3864"/>
          <text x={ox+6} y={oy-6} fontSize="10" fill="#1F3864">O</text>

          {/* Formula box */}
          <rect x={10} y={10} width={185} height={52} rx={4} fill="#fff" stroke="#ccd9ea"/>
          <text x={102} y={28}  textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Eigenvalue equation:</text>
          <text x={102} y={46}  textAnchor="middle" fontSize="12" fill="#2E75B6" fontFamily="'Courier New',monospace">[A]v = λv</text>
          <text x={102} y={58}  textAnchor="middle" fontSize="10" fill="#888">det([A]−λ[I]) = 0</text>

          {/* Legend */}
          <line x1={310} y1={22} x2={340} y2={22} stroke="#27ae60" strokeWidth="2.5"/>
          <text x={345} y={26} fontSize="11" fill="#27ae60">Eigenvector (before/after)</text>
          <line x1={310} y1={38} x2={340} y2={38} stroke="#c0392b" strokeWidth="2.5"/>
          <text x={345} y={42} fontSize="11" fill="#c0392b">Normal vector (before/after)</text>
          <line x1={310} y1={54} x2={340} y2={54} stroke="#555" strokeWidth="1.5" strokeDasharray="5,3"/>
          <text x={345} y={58} fontSize="11" fill="#555">After transformation</text>
        </svg>
      </div>
      <div style={S.dCaption}>
        An eigenvector only stretches (or flips) under transformation — it never changes direction. The eigenvalue λ is the scale factor. Normal vectors both stretch and rotate.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — ME Applications Panel
// ─────────────────────────────────────────────────────────────────
function DiagramMEApplications() {
  const W=540, H=280;

  // FEA stiffness matrix (3x3 banded)
  const K = [['2k','-k','0'],['-k','2k','-k'],['0','-k','2k']];
  // Robot transform (simplified 3x3 rotation + translation)
  const T = [['cos θ','-sin θ','x'],['sin θ','cos θ','y'],['0','0','1']];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Matrices in Mechanical Engineering</div>
      <div style={{ textAlign:'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth:'100%' }}>

          {/* ── FEA Stiffness Matrix ── */}
          <text x={130} y={22} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">FEA Stiffness Matrix [K]</text>

          {K.map((row, r) => row.map((val, c) => {
            const x0=20, y0=32, cW=70, cH=36;
            const cx = x0+c*cW+cW/2, cy = y0+r*cH+cH/2;
            const isDiag = r===c;
            return (
              <g key={`k${r}${c}`}>
                <rect x={x0+c*cW+2} y={y0+r*cH+2} width={cW-4} height={cH-4} rx={3}
                  fill={isDiag?'#d0e8ff':'#fff'} stroke="#aac" strokeWidth="1"/>
                <text x={cx} y={cy+5} textAnchor="middle" fontSize="12" fill={isDiag?'#2E75B6':'#1F3864'}
                  fontWeight={isDiag?'700':'400'} fontFamily="'Courier New',monospace">{val}</text>
              </g>
            );
          }))}
          <Bracket x={16}  y={28} h={3*36+8} side="left"  color="#2E75B6"/>
          <Bracket x={234} y={28} h={3*36+8} side="right" color="#2E75B6"/>

          {/* FEA annotation */}
          <text x={130} y={152} textAnchor="middle" fontSize="10" fill="#555">Banded structure — each node only</text>
          <text x={130} y={165} textAnchor="middle" fontSize="10" fill="#555">{"connects to neighbors. [K]{x}={F}"}</text>
          <rect x={10} y={175} width={240} height={52} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
          <text x={130} y={192} textAnchor="middle" fontSize="10" fill="#1F3864" fontWeight="700">Spring-mass system:</text>
          <text x={130} y={206} textAnchor="middle" fontSize="10" fill="#2E75B6" fontFamily="'Courier New',monospace">{"[K]{u} = {F}"}</text>
          <text x={130} y={220} textAnchor="middle" fontSize="10" fill="#555">{"Solve for displacements {u}"}</text>
          <text x={130} y={234} textAnchor="middle" fontSize="10" fill="#888">Appears in every FEA solver</text>

          {/* Divider */}
          <line x1={270} y1={15} x2={270} y2={H-15} stroke="#ddd" strokeWidth="1" strokeDasharray="4,3"/>

          {/* ── Transformation Matrix ── */}
          <text x={405} y={22} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Robot Arm Transform [T]</text>

          {T.map((row, r) => row.map((val, c) => {
            const x0=280, y0=32, cW=78, cH=36;
            const cx = x0+c*cW+cW/2, cy = y0+r*cH+cH/2;
            const isRot  = r<2 && c<2;
            const isTrans = c===2 && r<2;
            const fill = isRot ? '#ffd0d0' : isTrans ? '#d4edda' : '#f5f5f5';
            return (
              <g key={`t${r}${c}`}>
                <rect x={x0+c*cW+2} y={y0+r*cH+2} width={cW-4} height={cH-4} rx={3}
                  fill={fill} stroke="#aaa" strokeWidth="1"/>
                <text x={cx} y={cy+5} textAnchor="middle" fontSize="11"
                  fill={isRot?'#c0392b':isTrans?'#27ae60':'#888'}
                  fontWeight={isRot||isTrans?'700':'400'} fontFamily="'Courier New',monospace">{val}</text>
              </g>
            );
          }))}
          <Bracket x={276} y={28} h={3*36+8} side="left"  color="#1F3864"/>
          <Bracket x={518} y={28} h={3*36+8} side="right" color="#1F3864"/>

          {/* Legend */}
          <rect x={280} y={152} width={16} height={12} rx={2} fill="#ffd0d0" stroke="#c0392b" strokeWidth="1"/>
          <text x={302} y={162} fontSize="10" fill="#c0392b" fontWeight="700">Rotation (R)</text>
          <rect x={390} y={152} width={16} height={12} rx={2} fill="#d4edda" stroke="#27ae60" strokeWidth="1"/>
          <text x={412} y={162} fontSize="10" fill="#27ae60" fontWeight="700">Translation (t)</text>

          <rect x={280} y={172} width={240} height={58} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
          <text x={400} y={189} textAnchor="middle" fontSize="10" fill="#1F3864" fontWeight="700">Homogeneous transform:</text>
          <text x={400} y={203} textAnchor="middle" fontSize="10" fill="#2E75B6" fontFamily="'Courier New',monospace">{"[T] = [ R | t ]"}</text>
          <text x={400} y={217} textAnchor="middle" fontSize="10" fill="#555">{"Chain joints: T_total = T1×T2×T3"}</text>
          <text x={400} y={231} textAnchor="middle" fontSize="10" fill="#888">Used in forward kinematics (robotics)</text>

          {/* Bottom note */}
          <rect x={10} y={240} width={520} height={32} rx={4} fill="#fff7ee" stroke="#e07b00" strokeWidth="1"/>
          <text x={270} y={255} textAnchor="middle" fontSize="10" fill="#e07b00" fontWeight="700">Both are solved the same way:  find unknowns using matrix inversion or Gaussian elimination</text>
          <text x={270} y={268} textAnchor="middle" fontSize="10" fill="#888">In practice: MATLAB \ operator or numpy.linalg.solve() — never invert large matrices by hand</text>
        </svg>
      </div>
      <div style={S.dCaption}>
        Left: the stiffness matrix from a 3-node spring system — the core structure repeated in every FEA solver. Right: a 2D rigid body transformation matrix combining rotation and translation.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function Matrices(props) {
  return (
    <div style={S.wrapper}>
      {/* <SectionTitle title={props.topicId}/> */}
      <h1 style={S.h1}>Matrices</h1>

      <p style={S.p}>
        A matrix is a rectangular array of numbers organized into rows and columns. In mechanical engineering, matrices are the backbone of structural analysis (FEA), coordinate transformations, robotics kinematics, and solving large systems of equations. Understanding matrix operations lets you work with — and read the output of — every major engineering software tool.
      </p>

      <InfoBox
        title="Notation:  [A]_(m×n)  —  m rows, n columns, element a_(ij) at row i, column j"
        items={[
          "Square matrix: m = n  (most useful — can have a determinant and inverse)",
          "Row vector: 1×n matrix.   Column vector: m×1 matrix.",
          "Element aᵢⱼ: subscript is always row first, column second.",
        ]}
      />

      {/* Anatomy */}
      <h2 style={S.h2}>Matrix Structure</h2>
      <DiagramAnatomy />

      <Table2 rows={[
        ["Notation",          "[A]_(m×n) — m rows, n columns\na_(ij) = element in row i, column j"],
        ["Square Matrix",     "m = n — necessary for determinants, inverses, and eigenvalues"],
        ["Diagonal Matrix",   "Non-zero only on main diagonal (i=j). Easy to invert."],
        ["Identity Matrix",   "[I]: 1s on diagonal, 0s elsewhere. [A][I] = [A]"],
        ["Transpose [A]ᵀ",   "Swap rows and columns: (aᵢⱼ)ᵀ = aⱼᵢ\n(AB)ᵀ = BᵀAᵀ"],
        ["Symmetric Matrix",  "[A] = [A]ᵀ — stiffness matrices in FEA are always symmetric"],
      ]}/>

      {/* Operations */}
      <h2 style={S.h2}>Matrix Operations</h2>
      <DiagramMultiplication />

      <Table2 rows={[
        ["Addition / Subtraction", "[A] + [B] = [C]  where  cᵢⱼ = aᵢⱼ + bᵢⱼ\nRequires identical dimensions."],
        ["Scalar Multiplication",  "k[A] — multiply every element by scalar k"],
        ["Matrix Multiplication",  "[A]_(m×n) × [B]_(n×p) = [C]_(m×p)\ncᵢⱼ = Σₖ aᵢₖ · bₖⱼ   (inner dimensions must match)\nNote: AB ≠ BA in general"],
        ["NOT commutative",        "Order matters: AB ≠ BA in general\nBut: (AB)C = A(BC)  — associative"],
      ]}/>

      {/* Determinant */}
      <h2 style={S.h2}>Determinant</h2>
      <DiagramDeterminant />

      <Table2 rows={[
        ["2×2 Determinant",   "det([a,b; c,d]) = ad − bc"],
        ["3×3 Determinant",   "Cofactor expansion along row 1:\ndet = a₁₁(a₂₂a₃₃−a₂₃a₃₂) − a₁₂(a₂₁a₃₃−a₂₃a₃₁) + a₁₃(a₂₁a₃₂−a₂₂a₃₁)"],
        ["det = 0 means...",  "Rows are linearly dependent — no unique solution exists.\nMatrix is singular — cannot be inverted."],
        ["det ≠ 0 means...",  "Matrix is invertible. System [A]{x}={b} has a unique solution."],
        ["Geometric meaning", "2D: |det| = area of parallelogram formed by row vectors\n3D: |det| = volume of parallelepiped"],
      ]}/>

      {/* Inverse */}
      <h2 style={S.h2}>Inverse Matrix</h2>
      <DiagramInverse />

      <Table2 rows={[
        ["Definition",        "[A][A]⁻¹ = [A]⁻¹[A] = [I]\nExists only when det([A]) ≠ 0"],
        ["2×2 Formula",       "For [A] = [a,b; c,d]:\n[A]⁻¹ = (1/det) × [d, −b; −c, a]"],
        ["Solving systems",   "[A]{x} = {b}   →   {x} = [A]⁻¹{b}\nCore operation in FEA, statics, structural analysis"],
        ["Practical note",    "Never invert large matrices by hand — use Gaussian elimination,\nMATLAB: x = A\\b    Python: x = np.linalg.solve(A, b)"],
      ]}/>

      {/* Eigenvalues */}
      <h2 style={S.h2}>Eigenvalues & Eigenvectors</h2>
      <DiagramEigenvalue />

      <InfoBox
        title="Eigenvalue Equation:  [A]{v} = λ{v}   →   det([A] − λ[I]) = 0"
        items={[
          "Solve the characteristic polynomial det([A]−λ[I])=0 to find eigenvalues λ.",
          "For each λ, solve ([A]−λ[I]){v}={0} to find the corresponding eigenvector {v}.",
          "An n×n matrix has n eigenvalues (real or complex, counting multiplicity).",
        ]}
      />

      <Table2 rows={[
        ["Eigenvalue λ",       "The scale factor — how much the eigenvector stretches under [A]"],
        ["Eigenvector {v}",    "The special direction that only scales (never rotates) under [A]"],
        ["Finding eigenvalues","det([A] − λ[I]) = 0  →  solve the characteristic polynomial for λ"],
        ["Vibrations",         "Natural frequencies ωₙ come from eigenvalues of the system:\n[K]{x} = ω²[M]{x}  →  det([K]−ω²[M]) = 0\nEigenvectors = mode shapes"],
        ["Buckling",           "Critical buckling loads are eigenvalues of [K]{u} = λ[Kg]{u}"],
        ["Principal stresses", "Eigenvalues of the stress tensor = principal stresses σ₁, σ₂, σ₃\nEigenvectors = principal directions"],
      ]}/>

      {/* ME Applications */}
      <h2 style={S.h2}>Mechanical Engineering Applications</h2>
      <DiagramMEApplications />

      <InfoBox
        title="Key Takeaway — Where You Will Actually Use This"
        items={[
          "FEA (ANSYS, Abaqus, SolidWorks Simulation): entire solver is [K]{u}={F} — you're solving a massive matrix equation every run.",
          "Robotics & CNC: every joint transformation is a matrix multiply. Chain them together for full forward kinematics.",
          "Vibration analysis: natural frequencies and mode shapes = eigenvalue problem on the mass and stiffness matrices.",
          "Stress analysis: rotating stress tensors to find principal stresses = eigenvector problem.",
          "Controls: state-space representation [ẋ]=[A]{x}+[B]{u} — stability depends on eigenvalues of [A].",
        ]}
      />
    </div>
  );
}
